import React from 'react';
import { useIntl } from 'react-intl';
import { ArrowRight, CaretDown, CaretRight, Check, Minus, X } from '@phosphor-icons/react';
import type { DisplayJob } from '../../../../../../types';
import type { SearchInferenceBullet } from '../../../../../../types/job-match';
import type { MatchScoreBreakdown } from '../../../../../../utils/data/calculate-job-match-score';
import {
  buildFitLedger,
  buildSearchFitRows,
  FIT_GAP_THRESHOLD,
  FIT_STRENGTH_THRESHOLD,
  type FitCell,
  type FitRowKey,
  type SearchFitRowKey,
} from '../../../../../../utils/job-match/build-fit-ledger';
import { formatSalaryLabel } from '../../../../../../utils/data/format-job-card-labels';
import { mapMatchScoreToTier } from '../../../../../../utils/match-donut/mapMatchScoreToTier';
import { brandAccentOnDark } from '../../../../../../utils/brand-color/adjust-brand-color';
import { buildLocalizedProfileHref } from '../../../../../../utils/routing/build-localized-profile-href';
import { useDocumentDarkClass } from '../../../../../../hooks/useDocumentDarkClass';
import { useAppSelector } from '../../../../../../redux/hooks';
import { fixMojibake } from '../../../../../../utils/text/fix-mojibake';
import { Link } from '../../../../../../components/Link';
import { useFormatMessage } from '../../../../../../i18n-review/client/Msg';

export interface EditorialFitLedgerProps {
  displayJob: DisplayJob;
  matchScore: MatchScoreBreakdown;
  /** `recent-searches` when there's no profile yet and the score was estimated from search history. */
  matchScoreMeta?: { source: 'recent-searches'; bullets: SearchInferenceBullet[] } | null;
  /** The listing's pay in monthly EUR, as already converted for the page's salary label. */
  jobSalaryMonthly?: { min: number; max: number } | null;
  /** adjustedBrand.panel: the company's brand colour as a fill. */
  brandColor?: string;
  /** adjustedBrand.accentOnWhite: the same hue made legible as text on white; the dark-theme counterpart is derived here. */
  brandAccentOnWhite?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  onTailorCv?: (opts?: { source?: 'match_score' }) => void;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function brandAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : hex;
}

/** The brand colour laid at `alpha` over the card surface, as a solid colour (the spine markers' halo has to hide the line behind them, so it can't be translucent). */
function brandOnSurface(hex: string, alpha: number, surface: string): string {
  const b = hexToRgb(hex);
  const s = hexToRgb(surface);
  if (!b || !s) return surface;
  const mix = (x: number, y: number) => Math.round(y + (x - y) * alpha);
  return `rgb(${mix(b.r, s.r)}, ${mix(b.g, s.g)}, ${mix(b.b, s.b)})`;
}

function capitalizeFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

const KICKER_CLASS = 'text-[10.5px] font-extrabold uppercase tracking-wide text-[#8C8474]';
const INK_CLASS = 'text-[#16150F] dark:text-[#F3EFE4]';
const INK_SECONDARY_CLASS = 'text-[#4A4438] dark:text-[#C4A882]';
const MUTED_CLASS = 'text-[#8C8474]';

/** Per-row match state, used to colour the row and pick its icon. `strengths`/`gaps` on the
 * ledger already encode this (including the "missing language counts as a gap" override), so
 * non-search rows read it straight off the ledger instead of re-deriving it from the raw score. */
type FitRowState = 'match' | 'mismatch' | 'neutral';

/** Tailwind classes for a row's tint: green (jc-filter-job-*) for a match, terracotta
 * (jc-filter-company-*) for a mismatch, the plain card surface for anything in between. */
function rowToneClass(state: FitRowState): string {
  switch (state) {
    case 'match':
      return 'bg-jc-filter-job-bg text-jc-filter-job-text';
    case 'mismatch':
      return 'bg-jc-filter-company-bg text-jc-filter-company-text';
    default:
      return `bg-transparent ${INK_CLASS}`;
  }
}

/** Small inline-stroke status icon standing in for the old spine dot: a check for a match, a
 * cross for a mismatch, a dash for neutral. Colour is inherited (`currentColor`) from the row's
 * tone class so it always matches the row's text colour. */
function RowIcon({ state }: { state: FitRowState }): React.ReactElement {
  if (state === 'match') return <Check className="h-3.5 w-3.5 flex-none" weight="bold" aria-hidden />;
  if (state === 'mismatch') return <X className="h-3.5 w-3.5 flex-none" weight="bold" aria-hidden />;
  return <Minus className="h-3.5 w-3.5 flex-none opacity-50" weight="bold" aria-hidden />;
}

/** A small self-drawn ring with the letter grade; same tier and colour logic as the page's match donut. */
function GradeDisc({ overall, color, labelColor, title }: { overall: number; color: string; labelColor: string; title: string }): React.ReactElement {
  const size = 46;
  const stroke = 3.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex-none" style={{ width: size, height: size }} title={title} role="img" aria-label={`${title}: ${mapMatchScoreToTier(overall)}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill={brandAlpha(color, 0.08)} stroke={brandAlpha(color, 0.22)} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c - (overall / 100) * c}
          strokeLinecap="round"
        />
      </svg>
      <span
        className="absolute inset-0 grid place-items-center [font-family:'Space_Grotesk',sans-serif] text-[19px] font-bold leading-none"
        style={{ color: labelColor }}
      >
        {mapMatchScoreToTier(overall)}
      </span>
    </div>
  );
}

/**
 * TEC-512 editorial-rail redesign: the "fit ledger" replacing the old row of pass/fail pills.
 * Compacted (still TEC-512) from an earlier two-column spine layout that spread each dimension
 * over three lines: now each scored dimension is one tinted row (green for a match, terracotta
 * for a mismatch, the plain surface for anything in between, using the jc-filter-job/company
 * tokens), ordered by the user's own match weights so what they said matters most reads first.
 * A row shows the job's value first and the user's own value only when it disagrees ("Level
 * Mid-level, you: entry-level"); when they agree the row reads as just "Work style Onsite", with
 * no point restating the same thing twice. The one-sentence verdict above the rows is the only prose;
 * the footer still turns the gaps into the two or three actions that would actually move the score.
 * Per-row scores (and match/gap state) come straight from the breakdown the donut already uses
 * and the ledger already classifies; nothing here is rescored.
 */
export function EditorialFitLedger({
  displayJob,
  matchScore,
  matchScoreMeta,
  jobSalaryMonthly,
  brandColor,
  brandAccentOnWhite,
  expanded,
  onToggleExpanded,
  onTailorCv,
}: EditorialFitLedgerProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const isDark = useDocumentDarkClass();
  const profile = useAppSelector((state) => state.jobForm);
  const overall = Math.max(0, Math.min(100, matchScore.overall));
  const fromSearches = matchScoreMeta?.source === 'recent-searches';
  const profileHref = buildLocalizedProfileHref(intl.locale);
  // One accent per theme, each guaranteed to read against its surface. The raw panel colour is a
  // background colour (near-black for every neutral-fallback brand) and would vanish as a stroke
  // or as text on the dark card, so it is never painted directly.
  const accent = isDark ? brandAccentOnDark(brandColor) : brandAccentOnWhite || brandColor || '#6E4F00';
  const surface = isDark ? '#1F1509' : '#FFFFFF';
  // The whole panel sits on a faint wash of the brand colour; solid, not translucent, so the
  // marker halos (which have to cover the spine line) can be painted in exactly the same colour.
  const panelBg = brandOnSurface(accent, isDark ? 0.1 : 0.06, surface);
  const inkColor = isDark ? '#F3EFE4' : '#16150F';

  const ledger = React.useMemo(
    () => (fromSearches ? null : buildFitLedger({ job: displayJob, profile, breakdown: matchScore, jobSalaryMonthly })),
    [fromSearches, displayJob, profile, matchScore, jobSalaryMonthly],
  );
  const searchRows = React.useMemo(
    () => (fromSearches ? buildSearchFitRows(displayJob, matchScore, matchScoreMeta?.bullets ?? []) : []),
    [fromSearches, displayJob, matchScore, matchScoreMeta],
  );

  // Row vocabulary. `label` is the short spine label; `noun` is the lowercase mid-sentence form
  // used inside the verdict ("Pay, location and working style line up...").
  const rowLabel: Record<FitRowKey | SearchFitRowKey, string> = {
    title: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.title', defaultMessage: 'Role', description: 'Spine label for the job-title row of the fit ledger on the job page' }),
    roleLevel: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.roleLevel', defaultMessage: 'Level', description: 'Spine label for the seniority row of the fit ledger' }),
    location: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.location', defaultMessage: 'Location', description: 'Spine label for the location row of the fit ledger' }),
    remoteness: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.remoteness', defaultMessage: 'Work style', description: 'Spine label for the remote/hybrid/onsite row of the fit ledger' }),
    jobType: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.jobType', defaultMessage: 'Contract', description: 'Spine label for the job-type row of the fit ledger' }),
    salary: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.salary', defaultMessage: 'Pay', description: 'Spine label for the salary row of the fit ledger' }),
    languages: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.languages', defaultMessage: 'Languages', description: 'Spine label for the languages row of the fit ledger' }),
    vacation: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.vacation', defaultMessage: 'Time off', description: 'Spine label for the vacation-weeks row of the fit ledger' }),
    benefits: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.benefits', defaultMessage: 'Benefits', description: 'Spine label for the benefits row of the fit ledger' }),
    keywords: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.keywords', defaultMessage: 'Keywords', description: 'Spine label for the search-keywords row of the search-inferred fit ledger' }),
    searchLocation: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.location', defaultMessage: 'Location' }),
    searchRemoteness: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.remoteness', defaultMessage: 'Work style' }),
    searchRoles: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.searchRoles', defaultMessage: 'Role & field', description: 'Spine label for the role-filters row of the search-inferred fit ledger' }),
    searchCompany: formatMessage({ id: 'jobDetailPageV2.fitLedger.row.searchCompany', defaultMessage: 'Employer', description: 'Spine label for the employer row of the search-inferred fit ledger' }),
  };
  const rowNoun: Record<FitRowKey, string> = {
    title: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.title', defaultMessage: 'the role', description: 'Lowercase mid-sentence name of the job-title dimension, used in the fit verdict sentence' }),
    roleLevel: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.roleLevel', defaultMessage: 'seniority', description: 'Lowercase mid-sentence name of the role-level dimension, fit verdict sentence' }),
    location: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.location', defaultMessage: 'location', description: 'Lowercase mid-sentence name of the location dimension, fit verdict sentence' }),
    remoteness: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.remoteness', defaultMessage: 'working style', description: 'Lowercase mid-sentence name of the remoteness dimension, fit verdict sentence' }),
    jobType: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.jobType', defaultMessage: 'contract type', description: 'Lowercase mid-sentence name of the job-type dimension, fit verdict sentence' }),
    salary: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.salary', defaultMessage: 'pay', description: 'Lowercase mid-sentence name of the salary dimension, fit verdict sentence' }),
    languages: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.languages', defaultMessage: 'languages', description: 'Lowercase mid-sentence name of the languages dimension, fit verdict sentence' }),
    vacation: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.vacation', defaultMessage: 'time off', description: 'Lowercase mid-sentence name of the vacation dimension, fit verdict sentence' }),
    benefits: formatMessage({ id: 'jobDetailPageV2.fitLedger.noun.benefits', defaultMessage: 'benefits', description: 'Lowercase mid-sentence name of the benefits dimension, fit verdict sentence' }),
  };

  const tierHeadline = (() => {
    switch (mapMatchScoreToTier(overall)) {
      case 'A':
        return formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.A', defaultMessage: 'Strong fit', description: 'Fit ledger headline for an A-grade match' });
      case 'B':
        return formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.B', defaultMessage: 'Good fit', description: 'Fit ledger headline for a B-grade match' });
      case 'C':
        return formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.C', defaultMessage: 'Mixed fit', description: 'Fit ledger headline for a C-grade match' });
      case 'D':
        return formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.D', defaultMessage: 'Weak fit', description: 'Fit ledger headline for a D-grade match' });
      default:
        return formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.F', defaultMessage: 'Not a fit on paper', description: 'Fit ledger headline for an F-grade match' });
    }
  })();

  const verdictSubline = (() => {
    if (fromSearches) {
      return formatMessage({
        id: 'matchDonut.searchInference.intro',
        defaultMessage: 'We don’t have your profile yet, so this score estimates fit using filters and keywords from searches you’ve run on JobCrawls (recent activity counts more).',
      });
    }
    if (!ledger) return '';
    const strengths = ledger.strengths.slice(0, 3).map((k) => rowNoun[k]);
    const gaps = ledger.gaps.slice(0, 2).map((k) => rowNoun[k]);
    const parts: string[] = [];
    if (strengths.length > 0) {
      parts.push(
        capitalizeFirst(
          formatMessage(
            {
              id: 'jobDetailPageV2.fitLedger.verdict.strengths',
              defaultMessage: '{list} {count, plural, one {lines up} other {line up}} with what you want.',
              description: 'Fit verdict sentence listing the dimensions that match; {list} is a localized "a, b and c" list of lowercase dimension nouns',
            },
            { list: intl.formatList(strengths, { type: 'conjunction' }), count: strengths.length },
          ),
        ),
      );
    }
    if (gaps.length > 0) {
      parts.push(
        formatMessage(
          {
            id: 'jobDetailPageV2.fitLedger.verdict.gaps',
            defaultMessage: '{count, plural, one {The gap: {list}.} other {The gaps: {list}.}}',
            description: 'Fit verdict sentence naming the dimensions that fall short; {list} is a localized list of lowercase dimension nouns',
          },
          { list: intl.formatList(gaps, { type: 'conjunction' }), count: gaps.length },
        ),
      );
    } else if (strengths.length > 0) {
      parts.push(formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.noGaps', defaultMessage: 'Nothing stands out as a gap.', description: 'Fit verdict follow-up when no dimension scores as a gap' }));
    }
    if (parts.length === 0) {
      parts.push(formatMessage({ id: 'jobDetailPageV2.fitLedger.verdict.allMiddle', defaultMessage: 'Every factor lands somewhere in the middle.', description: 'Fit verdict when no dimension is clearly strong or clearly weak' }));
    }
    return parts.join(' ');
  })();

  const money = (min: number, max: number) =>
    formatMessage(
      { id: 'jobDetailPageV2.fitLedger.perMonth', defaultMessage: '{amount} / mo', description: 'Monthly pay figure in the fit ledger; {amount} is an already formatted euro amount or range' },
      { amount: formatSalaryLabel(min, max, intl.locale) },
    );

  const emptyYou = formatMessage({ id: 'jobDetailPageV2.fitLedger.cell.notSet', defaultMessage: 'Not set', description: 'Fit ledger cell shown when the user’s profile has no value for this dimension' });
  const emptyJob = formatMessage({ id: 'jobDetailPageV2.fitLedger.cell.notStated', defaultMessage: 'Not stated', description: 'Fit ledger cell shown when the listing has no value for this dimension' });
  const moreLabel = (n: number) => formatMessage({ id: 'jobDetailPageV2.fitLedger.cell.more', defaultMessage: '+{count} more', description: 'Suffix after a truncated list in a fit ledger cell' }, { count: n });

  /** Flattens a cell to plain text: used both for the truncated row's `title` tooltip and to
   * decide whether the "you" side is worth printing (it is only shown when it differs from the
   * job's value; see the row-building code below). Mirrors renderCell's cases but returns a
   * string instead of JSX, and reuses its exact message ids so no new strings are needed for it. */
  const cellText = (cell: FitCell, emptyLabel: string): string => {
    switch (cell.kind) {
      case 'empty':
        return emptyLabel;
      case 'text':
        return fixMojibake(cell.text);
      case 'list':
        return cell.items.map((item) => fixMojibake(item)).join(', ') + (cell.more > 0 ? ` ${moreLabel(cell.more)}` : '');
      case 'money':
        return money(cell.min, cell.max);
      case 'weeks':
        return formatMessage(
          { id: 'jobDetailPageV2.fitLedger.cell.weeks', defaultMessage: '{weeks, plural, one {# week} other {# weeks}} / year', description: 'Vacation figure in a fit ledger cell' },
          { weeks: cell.weeks },
        );
      case 'count':
        return formatMessage(
          { id: 'jobDetailPageV2.fitLedger.cell.benefitsNow', defaultMessage: '{count, plural, one {# benefit now} other {# benefits now}}', description: 'Fit ledger cell summarising how many benefits the user’s current job has' },
          { count: cell.count },
        );
      case 'chips':
        return cell.chips.map((chip) => fixMojibake(chip.label)).join(', ') + (cell.more > 0 ? ` ${moreLabel(cell.more)}` : '');
      default:
        return '';
    }
  };

  const renderCell = (cell: FitCell, side: 'you' | 'job'): React.ReactNode => {
    switch (cell.kind) {
      case 'empty':
        return <span className={`font-medium italic ${MUTED_CLASS}`}>{side === 'you' ? emptyYou : emptyJob}</span>;
      case 'text':
        return fixMojibake(cell.text);
      case 'list':
        return (
          <>
            {cell.items.map((item) => fixMojibake(item)).join(', ')}
            {cell.more > 0 ? <span className={`ml-1 font-medium ${MUTED_CLASS}`}>{moreLabel(cell.more)}</span> : null}
          </>
        );
      case 'money':
        return money(cell.min, cell.max);
      case 'weeks':
        return formatMessage(
          { id: 'jobDetailPageV2.fitLedger.cell.weeks', defaultMessage: '{weeks, plural, one {# week} other {# weeks}} / year', description: 'Vacation figure in a fit ledger cell' },
          { weeks: cell.weeks },
        );
      case 'count':
        return formatMessage(
          { id: 'jobDetailPageV2.fitLedger.cell.benefitsNow', defaultMessage: '{count, plural, one {# benefit now} other {# benefits now}}', description: 'Fit ledger cell summarising how many benefits the user’s current job has' },
          { count: cell.count },
        );
      case 'chips':
        return (
          <span className={`inline-flex flex-wrap gap-1.5 ${side === 'you' ? 'sm:justify-end' : ''}`}>
            {cell.chips.map((chip) => {
              const base = 'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11.5px] font-semibold leading-tight';
              if (chip.state === 'missing') {
                return (
                  <span key={chip.label} className={`${base} border border-dashed border-[#B0A896] dark:border-[#8C8474] ${MUTED_CLASS}`}>
                    {fixMojibake(chip.label)}
                  </span>
                );
              }
              if (chip.state === 'gain') {
                return (
                  <span key={chip.label} className={base} style={{ background: brandAlpha(accent, 0.14), color: accent }}>
                    +&nbsp;{fixMojibake(chip.label)}
                  </span>
                );
              }
              return (
                <span key={chip.label} className={`${base} border ${INK_CLASS}`} style={{ borderColor: brandAlpha(accent, 0.35) }}>
                  {fixMojibake(chip.label)}
                </span>
              );
            })}
            {cell.more > 0 ? <span className={`self-center text-[11px] font-medium ${MUTED_CLASS}`}>{moreLabel(cell.more)}</span> : null}
          </span>
        );
      default:
        return null;
    }
  };

  /** Match state per row drives the row's tint and icon. Non-search rows read it straight off
   * the ledger's `strengths`/`gaps` (which already fold in the "missing a required language
   * counts as a gap even if its score is mid-range" rule); search-inferred rows have no such
   * lists, so they fall back to the same score thresholds the ledger itself uses to build them. */
  const matchStateFor = (key: FitRowKey | SearchFitRowKey, score: number): FitRowState => {
    if (!fromSearches && ledger) {
      if (ledger.strengths.includes(key as FitRowKey)) return 'match';
      if (ledger.gaps.includes(key as FitRowKey)) return 'mismatch';
      return 'neutral';
    }
    if (score >= FIT_STRENGTH_THRESHOLD) return 'match';
    if (score < FIT_GAP_THRESHOLD) return 'mismatch';
    return 'neutral';
  };

  type LedgerLine = { key: string; label: string; you: FitCell; job: FitCell; state: FitRowState };
  const lines: LedgerLine[] = fromSearches
    ? searchRows.map((r) => ({ key: r.key, label: rowLabel[r.key], you: r.you, job: r.job, state: matchStateFor(r.key, r.score) }))
    : (ledger?.rows ?? []).map((r) => ({ key: r.key, label: rowLabel[r.key], you: r.you, job: r.job, state: matchStateFor(r.key, r.score) }));

  // Footer actions: only the ones that would actually move this score.
  const actions: Array<{ key: string; label: string; href?: string; onClick?: () => void }> = [];
  if (fromSearches) {
    actions.push({
      key: 'profile',
      label: formatMessage({ id: 'jobDetailPageV2.fitLedger.action.fillProfile', defaultMessage: 'Fill in your profile for a real comparison', description: 'Fit ledger action when the score is only estimated from searches' }),
      href: profileHref,
    });
  } else {
    if (ledger && ledger.missingLanguages.length > 0) {
      actions.push({
        key: 'languages',
        label: formatMessage(
          { id: 'jobDetailPageV2.fitLedger.action.addLanguage', defaultMessage: 'Speak {list}? Add it to your profile', description: 'Fit ledger action when the listing requires a language the profile does not list; {list} is a localized list of language names' },
          { list: intl.formatList(ledger.missingLanguages, { type: 'disjunction' }) },
        ),
        href: `${profileHref}#section-current-role`,
      });
    }
    if (onTailorCv) {
      actions.push({
        key: 'tailor',
        label: formatMessage({ id: 'jobDetailPageV2.fitLedger.action.tailorCv', defaultMessage: 'Tailor your CV to this role', description: 'Fit ledger action opening the CV tailoring tool' }),
        onClick: () => onTailorCv({ source: 'match_score' }),
      });
    }
    actions.push({
      key: 'weights',
      label: formatMessage({ id: 'jobDetailPageV2.fitLedger.action.weights', defaultMessage: 'Change what matters most to you', description: 'Fit ledger action linking to the match-weight sliders on the profile page' }),
      href: `${profileHref}#section-match-preferences`,
    });
  }

  const kicker = fromSearches
    ? formatMessage({ id: 'jobDetailPageV2.fitLedger.kicker.searches', defaultMessage: 'Estimated from your searches', description: 'Small uppercase label above the fit ledger when the score comes from search history, not a profile' })
    : formatMessage({ id: 'jobDetailPageV2.fitLedger.kicker', defaultMessage: 'Your fit', description: 'Small uppercase label above the fit ledger on the job page' });

  const actionClass = 'inline-flex items-center gap-1 text-[12.5px] font-bold underline-offset-2 hover:underline cursor-pointer bg-transparent border-0 p-0';

  return (
    <section
      id="match-breakdown-section"
      className="scroll-mt-24 rounded-2xl border px-5 py-5 md:px-7 md:py-6"
      style={{ background: panelBg, borderColor: brandAlpha(accent, 0.22), ['--ledger-bg' as string]: panelBg, ['--ledger-spine' as string]: brandAlpha(accent, 0.3) }}
      aria-labelledby="fit-ledger-heading"
    >
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={KICKER_CLASS}>{kicker}</div>
          <h3 id="fit-ledger-heading" className={`m-0 mt-1 [font-family:'Space_Grotesk',sans-serif] text-[21px] md:text-[23px] font-bold leading-tight ${INK_CLASS}`}>
            {tierHeadline}
          </h3>
          {verdictSubline ? <p className={`m-0 mt-1.5 max-w-[60ch] text-[13px] leading-relaxed ${INK_SECONDARY_CLASS}`}>{verdictSubline}</p> : null}
        </div>
        <div className="flex flex-none items-center gap-2">
          <GradeDisc
            overall={overall}
            color={accent}
            labelColor={inkColor}
            title={formatMessage({ id: 'jobDetailPageV2.matchBreakdown.overallLabel', defaultMessage: 'overall match' })}
          />
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            aria-controls="fit-ledger-body"
            className={`rounded-lg p-1 ${INK_SECONDARY_CLASS} hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer`}
            aria-label={
              expanded
                ? formatMessage({ id: 'jobDetailPage.matchBreakdown.collapse', defaultMessage: 'Collapse match breakdown' })
                : formatMessage({ id: 'jobDetailPage.matchBreakdown.expand', defaultMessage: 'Expand match breakdown' })
            }
          >
            {expanded ? <CaretDown className="h-4 w-4" /> : <CaretRight className="h-4 w-4" />}
          </button>
        </div>
      </header>

      {/* Stays mounted and is toggled via `hidden`, not conditionally rendered: a live text node
          here can get rewritten by Google Translate's <font> wrapper behind React's back, and
          mounting/unmounting at that DOM position is what triggers the removeChild crash (see
          install-translate-safe-dom-patches.ts). */}
      <div id="fit-ledger-body" className={expanded ? '' : 'hidden'} aria-hidden={expanded ? undefined : true}>
        {lines.length > 0 ? (
          <ol className="m-0 mt-4 flex list-none flex-col gap-1 p-0">
            {lines.map((line) => {
              const jobText = cellText(line.job, emptyJob);
              const youText = cellText(line.you, emptyYou);
              // The "you: …" suffix only earns its place when the listing actually states
              // something to compare against and the two sides read differently; a job that
              // doesn't state a dimension has nothing for the user's value to disagree with.
              const showYou = line.job.kind !== 'empty' && youText.trim().toLowerCase() !== jobText.trim().toLowerCase();
              return (
                <li key={line.key} className={`flex items-center gap-2 rounded-lg px-2.5 py-[7px] ${rowToneClass(line.state)}`}>
                  <RowIcon state={line.state} />
                  <span className="flex-none whitespace-nowrap text-[10px] font-extrabold uppercase tracking-wide opacity-70">{line.label}</span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-bold" title={jobText}>
                    {renderCell(line.job, 'job')}
                  </span>
                  {showYou ? (
                    <span className="min-w-0 max-w-[42%] flex-none truncate text-[11px] font-medium opacity-65" title={youText}>
                      {formatMessage(
                        {
                          id: 'jobDetailPageV2.fitLedger.cell.youSuffix',
                          defaultMessage: ', you: {value}',
                          description: 'Muted suffix appended to a compact fit-ledger row when the user’s own value differs from the job’s; {value} is the already-formatted user value. Includes the leading comma that separates it from the job value.',
                        },
                        { value: youText },
                      )}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}

        <footer className="mt-4 border-t pt-4" style={{ borderColor: brandAlpha(accent, 0.15) }}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {actions.map((action) =>
              action.href ? (
                <Link key={action.key} href={action.href} className={actionClass} style={{ color: accent }}>
                  {action.label}
                  <ArrowRight className="h-3.5 w-3.5" weight="bold" aria-hidden />
                </Link>
              ) : (
                <button key={action.key} type="button" onClick={action.onClick} className={actionClass} style={{ color: accent }}>
                  {action.label}
                  <ArrowRight className="h-3.5 w-3.5" weight="bold" aria-hidden />
                </button>
              ),
            )}
          </div>
          {!fromSearches && ledger ? (
            <p className={`m-0 mt-3 text-[11px] leading-relaxed ${MUTED_CLASS}`}>
              {ledger.usesDesiredJob
                ? formatMessage({ id: 'jobDetailPageV2.fitLedger.basis.desired', defaultMessage: 'Compared with the job you said you want on your profile; where you left something blank, your current role is used instead.', description: 'Fit ledger footnote when at least one row is based on the desired-job section of the profile' })
                : formatMessage({ id: 'jobDetailPageV2.fitLedger.basis.current', defaultMessage: 'Compared with your current role. Set a desired job on your profile to compare against what you actually want.', description: 'Fit ledger footnote when every row is based on the current-role section of the profile' })}
            </p>
          ) : null}
        </footer>
      </div>
    </section>
  );
}
